import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./lib/auth-context";
import { I18nProvider } from "./lib/i18n";
import { ProtectedRoute } from "./components/ProtectedRoute";
import MobileShell from "./components/MobileShell";

import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";

// Consumer
import ConsumerHome from "./pages/consumer/ConsumerHome";
import CreditProducts from "./pages/consumer/CreditProducts";
import LoanApply from "./pages/consumer/LoanApply";
import LoanDetail from "./pages/consumer/LoanDetail";
import ConsumerWallet from "./pages/consumer/ConsumerWallet";
import Activity from "./pages/consumer/Activity";
import ConsumerProfile from "./pages/consumer/ConsumerProfile";
import ScoreFactors from "./pages/consumer/ScoreFactors";
import Notifications from "./pages/consumer/Notifications";
import Savings from "./pages/consumer/Savings";
import Insurance from "./pages/consumer/Insurance";
import Investments from "./pages/consumer/Investments";
import Marketplace from "./pages/consumer/Marketplace";
import KycSubmit from "./pages/consumer/KycSubmit";
import ScoreBoosters from "./pages/consumer/ScoreBoosters";
import SplitPayApply from "./pages/consumer/SplitPayApply";
import SplitPayDetail from "./pages/consumer/SplitPayDetail";
import SplitPayList from "./pages/consumer/SplitPayList";

// Business
import BusinessHome from "./pages/business/BusinessHome";
import BusinessLoans from "./pages/business/BusinessLoans";
import BusinessLoanApply from "./pages/business/BusinessLoanApply";
import BusinessProducts from "./pages/business/BusinessProducts";
import BusinessFinance from "./pages/business/BusinessFinance";
import BusinessProfile from "./pages/business/BusinessProfile";
import BusinessCustomers from "./pages/business/BusinessCustomers";
import BusinessSuppliers from "./pages/business/BusinessSuppliers";
import BusinessExpenses from "./pages/business/BusinessExpenses";
import BusinessSales from "./pages/business/BusinessSales";
import Invoices from "./pages/business/Invoices";
import MerchantOS from "./pages/business/MerchantOS";
import AgroCreditApply from "./pages/business/AgroCreditApply";
import FleetFinanceApply from "./pages/business/FleetFinanceApply";

// Admin
import AdminShell from "./pages/admin/AdminShell";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminBusinesses from "./pages/admin/AdminBusinesses";
import AdminLoans from "./pages/admin/AdminLoans";
import AdminLoanDetail from "./pages/admin/AdminLoanDetail";
import AdminInsurance from "./pages/admin/AdminInsurance";
import AdminKyc from "./pages/admin/AdminKyc";
import {
    CreditEngine,
    AdminTransactions,
    AdminCollections,
    AdminReports,
    AdminRisk,
    AdminSettings,
    AdminLogs,
} from "./pages/admin/AdminMisc";

function App() {
    return (
        <I18nProvider>
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                    {/* Public */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />

                    {/* Consumer (protected) */}
                    <Route
                        path="/consumer"
                        element={
                            <ProtectedRoute role="consumer">
                                <MobileShell variant="consumer" />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<ConsumerHome />} />
                        <Route path="credit" element={<CreditProducts />} />
                        <Route path="credit/score" element={<ScoreFactors />} />
                        <Route path="credit/loans/:loanId" element={<LoanDetail />} />
                        <Route path="credit/SplitPay" element={<SplitPayApply />} />
                        <Route path="credit/:product" element={<LoanApply />} />
                        <Route path="splitpay" element={<SplitPayList />} />
                        <Route path="splitpay/:loanId" element={<SplitPayDetail />} />
                        <Route path="wallet" element={<ConsumerWallet />} />
                        <Route path="wallet/add" element={<ConsumerWallet />} />
                        <Route path="wallet/send" element={<ConsumerWallet />} />
                        <Route path="activity" element={<Activity />} />
                        <Route path="profile" element={<ConsumerProfile />} />
                        <Route path="notifications" element={<Notifications />} />
                        <Route path="savings" element={<Savings />} />
                        <Route path="insurance" element={<Insurance />} />
                        <Route path="investments" element={<Investments />} />
                        <Route path="shop" element={<Marketplace />} />
                        <Route path="kyc" element={<KycSubmit />} />
                        <Route path="boosters" element={<ScoreBoosters />} />
                    </Route>

                    {/* Business (protected) */}
                    <Route
                        path="/business"
                        element={
                            <ProtectedRoute role="business">
                                <MobileShell variant="business" />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<BusinessHome />} />
                        <Route path="business" element={<MerchantOS />} />
                        <Route path="finance" element={<BusinessFinance />} />
                        <Route path="loans" element={<BusinessLoans />} />
                        <Route path="loans/detail/:loanId" element={<LoanDetail />} />
                        <Route path="loans/AgroCredit" element={<AgroCreditApply />} />
                        <Route path="loans/FleetFinance" element={<FleetFinanceApply />} />
                        <Route path="credit/SplitPay" element={<SplitPayApply />} />
                        <Route path="loans/SplitPay" element={<SplitPayApply />} />
                        <Route path="splitpay" element={<SplitPayList />} />
                        <Route path="splitpay/:loanId" element={<SplitPayDetail />} />
                        <Route path="loans/:product" element={<BusinessLoanApply />} />
                        <Route path="notifications" element={<Notifications />} />
                        <Route path="more" element={<BusinessProfile />} />
                        <Route path="more/merchantos" element={<MerchantOS />} />
                        <Route path="more/products" element={<BusinessProducts />} />
                        <Route path="more/customers" element={<BusinessCustomers />} />
                        <Route path="more/suppliers" element={<BusinessSuppliers />} />
                        <Route path="more/expenses" element={<BusinessExpenses />} />
                        <Route path="more/sales" element={<BusinessSales />} />
                        <Route path="more/invoices" element={<Invoices />} />
                        <Route path="more/savings" element={<Savings />} />
                        <Route path="more/insurance" element={<Insurance />} />
                        <Route path="more/investments" element={<Investments />} />
                        <Route path="more/marketplace" element={<Marketplace />} />
                        <Route path="more/kyc" element={<KycSubmit />} />
                    </Route>

                    {/* Admin (protected) */}
                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute role="admin">
                                <AdminShell />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<AdminDashboard />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="businesses" element={<AdminBusinesses />} />
                        <Route path="loans" element={<AdminLoans />} />
                        <Route path="loans/:loanId" element={<AdminLoanDetail />} />
                        <Route path="insurance" element={<AdminInsurance />} />
                        <Route path="kyc" element={<AdminKyc />} />
                        <Route path="credit-engine" element={<CreditEngine />} />
                        <Route path="transactions" element={<AdminTransactions />} />
                        <Route path="collections" element={<AdminCollections />} />
                        <Route path="reports" element={<AdminReports />} />
                        <Route path="risk" element={<AdminRisk />} />
                        <Route path="settings" element={<AdminSettings />} />
                        <Route path="system-logs" element={<AdminLogs />} />
                    </Route>

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <Toaster richColors position="top-right" />
            </BrowserRouter>
            </AuthProvider>
        </I18nProvider>
    );
}

export default App;
